/**
 * Role-Permission Mapping
 * 
 * Centralized mapping of roles to their assigned permissions.
 * This is the single source of truth for access control.
 * 
 * Design: Each role maps to an array of permissions.
 * Future: Can be extended to support multiple roles per user.
 */

import { Role } from './roles';
import { Permission } from './permissions';
import {
  USER_CREATE,
  USER_EDIT,
  USER_DELETE,
  USER_VIEW,
  USER_LIST,
  GAME_CREATE,
  GAME_EDIT,
  GAME_DELETE,
  GAME_VIEW,
  GAME_LIST,
  GAME_OPEN,
  SODA_CREATE,
  SODA_EDIT,
  SODA_DELETE,
  SODA_VIEW,
  SODA_VIEW_ALL,
  SODA_VIEW_PARTNER_ONLY,
  PARTY_CREATE,
  PARTY_EDIT,
  PARTY_DELETE,
  PARTY_VIEW,
  PARTY_LIST,
  REPORT_VIEW,
  REPORT_LEDGER,
  REPORT_TRIAL_BALANCE,
  REPORT_HAVALA,
  REPORT_MATCH,
  HAVLA_CREATE,
  HAVLA_EDIT,
  HAVLA_DELETE,
  MATCH_RECALL,
  IMPORT_ACCOUNT,
  DATABASE_BACKUP,
  QUICK_ADD_PARTY,
  PARTNER_SUMMARY_VIEW,
  PARTNER_TRANSACTIONS_VIEW,
} from './permissions';

/**
 * Map of roles to their permissions
 * 
 * Security: Deny by default - only explicitly granted permissions are included
 */
/** Owner access without Soda, Select Match, or Match Recall. Soda Form Sequence stays Owner-only. */
const ANGADIYA_OWNER_PERMISSIONS: Permission[] = [
  USER_CREATE,
  USER_EDIT,
  USER_DELETE,
  USER_VIEW,
  USER_LIST,
  PARTY_CREATE,
  PARTY_EDIT,
  PARTY_DELETE,
  PARTY_VIEW,
  PARTY_LIST,
  REPORT_VIEW,
  REPORT_LEDGER,
  REPORT_TRIAL_BALANCE,
  REPORT_HAVALA,
  HAVLA_CREATE,
  HAVLA_EDIT,
  HAVLA_DELETE,
  IMPORT_ACCOUNT,
  DATABASE_BACKUP,
  QUICK_ADD_PARTY,
];

/** Same as Angadiya, without user management and without editing havalas. */
const ANGADIYA_USER_PERMISSIONS: Permission[] = ANGADIYA_OWNER_PERMISSIONS.filter(
  (permission) =>
    permission !== USER_CREATE &&
    permission !== USER_EDIT &&
    permission !== USER_DELETE &&
    permission !== USER_VIEW &&
    permission !== USER_LIST &&
    permission !== HAVLA_EDIT,
);

export const ROLE_PERMISSION_MAP: Record<Role, Permission[]> = {
  [Role.ADMIN]: [
    // Full access to everything
    USER_CREATE,
    USER_EDIT,
    USER_DELETE,
    USER_VIEW,
    USER_LIST,
    GAME_CREATE,
    GAME_EDIT,
    GAME_DELETE,
    GAME_VIEW,
    GAME_LIST,
    GAME_OPEN,
    SODA_CREATE,
    SODA_EDIT,
    SODA_DELETE,
    SODA_VIEW,
    SODA_VIEW_ALL,
    PARTY_CREATE,
    PARTY_EDIT,
    PARTY_DELETE,
    PARTY_VIEW,
    PARTY_LIST,
    REPORT_VIEW,
    REPORT_LEDGER,
    REPORT_TRIAL_BALANCE,
    REPORT_HAVALA,
    REPORT_MATCH,
    HAVLA_CREATE,
    HAVLA_EDIT,
    HAVLA_DELETE,
    MATCH_RECALL,
    IMPORT_ACCOUNT,
    DATABASE_BACKUP,
    QUICK_ADD_PARTY,
  ],

  [Role.OWNER]: [
    // Similar to Admin but may have some restrictions
    USER_CREATE,
    USER_EDIT,
    USER_DELETE,
    USER_VIEW,
    USER_LIST,
    GAME_CREATE,
    GAME_EDIT,
    GAME_DELETE,
    GAME_VIEW,
    GAME_LIST,
    GAME_OPEN,
    SODA_CREATE,
    SODA_EDIT,
    SODA_DELETE,
    SODA_VIEW,
    SODA_VIEW_ALL,
    PARTY_CREATE,
    PARTY_EDIT,
    PARTY_DELETE,
    PARTY_VIEW,
    PARTY_LIST,
    REPORT_VIEW,
    REPORT_LEDGER,
    REPORT_TRIAL_BALANCE,
    REPORT_HAVALA,
    REPORT_MATCH,
    HAVLA_CREATE,
    HAVLA_EDIT,
    HAVLA_DELETE,
    MATCH_RECALL,
    IMPORT_ACCOUNT,
    DATABASE_BACKUP,
    QUICK_ADD_PARTY,
  ],

  [Role.USER]: [
    // Standard user permissions
    GAME_VIEW,
    GAME_LIST,
    GAME_OPEN,
    SODA_CREATE,
    SODA_EDIT,
    SODA_VIEW,
    SODA_VIEW_ALL,
    PARTY_CREATE,
    PARTY_EDIT,
    PARTY_VIEW,
    PARTY_LIST,
    REPORT_VIEW,
    REPORT_LEDGER,
    REPORT_MATCH,
    HAVLA_CREATE,
    IMPORT_ACCOUNT,
    DATABASE_BACKUP,
  ],

  [Role.PARTNER]: [
    // Partner-specific permissions
    // Can view game list and open gameboard
    GAME_VIEW,
    GAME_LIST,
    GAME_OPEN,
    // Can only view partner-related sodas
    SODA_VIEW_PARTNER_ONLY,
    PARTNER_SUMMARY_VIEW,
    PARTNER_TRANSACTIONS_VIEW,
    // Limited report access
    REPORT_VIEW,
  ],

  [Role.ANGADIYA]: ANGADIYA_OWNER_PERMISSIONS,

  [Role.ANGADIYA_ADMIN]: [
    // Angadiya admin permissions - can manage users and parties like Admin/Owner
    USER_CREATE,
    USER_EDIT,
    USER_DELETE,
    USER_VIEW,
    USER_LIST,
    GAME_VIEW,
    GAME_LIST,
    GAME_OPEN,
    SODA_VIEW,
    SODA_VIEW_ALL,
    SODA_CREATE,
    SODA_EDIT,
    SODA_DELETE,
    PARTY_CREATE,
    PARTY_EDIT,
    PARTY_DELETE,
    PARTY_VIEW,
    PARTY_LIST,
    REPORT_VIEW,
    REPORT_LEDGER,
    REPORT_TRIAL_BALANCE,
    REPORT_HAVALA,
    HAVLA_CREATE,
    HAVLA_EDIT,
    HAVLA_DELETE,
    MATCH_RECALL,
    QUICK_ADD_PARTY,
  ],

  [Role.ANGADIYA_USER]: ANGADIYA_USER_PERMISSIONS,
};

/**
 * Get permissions for a given role
 * 
 * @param role - The role to get permissions for
 * @returns Array of permissions for the role, or empty array if role not found
 */
export const getPermissionsForRole = (role: Role | string): Permission[] => {
  if (role in ROLE_PERMISSION_MAP) {
    return ROLE_PERMISSION_MAP[role as Role];
  }
  // Deny by default - return empty array if role not found
  return [];
};

/**
 * Get permissions for multiple roles
 * 
 * @param roles - Array of roles
 * @returns Combined array of unique permissions
 */
export const getPermissionsForRoles = (roles: (Role | string)[]): Permission[] => {
  const permissionSet = new Set<Permission>();
  
  roles.forEach(role => {
    const permissions = getPermissionsForRole(role);
    permissions.forEach(permission => permissionSet.add(permission));
  });
  
  return Array.from(permissionSet);
};

// Ensure this file is treated as a module
export {};

